import React from 'react';
import { Button, Tooltip, Typography, Grid } from 'antd';
import { InfoCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Form, ProjectsConstructorForm, useAppFindFormik } from '@/components';
import { AddTableDto, ProjectDto } from '@/core';
import { TableFormInputs } from './TableFormInputs';
import { TableFormColumns } from './TableFormColumns';
import { TableTypesEnum, useSchemaTable, FormValueType } from './hooks';
import classes from './TableForm.module.scss';

const { useBreakpoint } = Grid;

type PropsType = {
  initialValue: FormValueType;
  formUid: string;
  handleCancel?: () => void;
  handleSubmit: (tableData: AddTableDto) => void;
  isEditForm?: boolean;
  projectData: ProjectDto;
  pickedColumns?: string[];
  setPickedColumns: (columns: string[]) => void;
  clearForm: () => void;
  pickedTableType: string;
  setPickedTableType: (type: string) => void;
  stopEditCard?: () => void;
};

export const TableForm = (props: PropsType) => {
  const {
    initialValue,
    formUid,
    handleCancel,
    handleSubmit,
    isEditForm,
    projectData,
    pickedColumns,
    setPickedColumns,
    clearForm,
    pickedTableType,
    setPickedTableType,
    stopEditCard,
  } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const form = useAppFindFormik(formUid);
  const schemaTable = useSchemaTable(projectData, initialValue);
  const screens = useBreakpoint();
  const isMobileScreen = !screens.sm;

  const handleChangeType = (newType: string) => setPickedTableType(newType);
  const handleChangePickedColumns = (pickedColumns: string[]) => setPickedColumns(pickedColumns);
  const handleDeleteColumn = (column: string) => {
    const filteredColumns = pickedColumns?.filter((x) => x !== column);
    form.current?.setFieldValue('columns', filteredColumns);
    setPickedColumns(filteredColumns ?? []);
  };

  return (
    <ProjectsConstructorForm<FormValueType>
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      schema={schemaTable}
      initialValues={initialValue}
      formUid={formUid}
      withoutControls
    >
      <div className={classes.tableForm}>
        <TableFormInputs
          projectData={projectData}
          handleChangePickedColumns={handleChangePickedColumns}
          handleChangeType={handleChangeType}
        />
        {pickedColumns?.length ? (
          <div className={classes.informNotification}>
            <InfoCircleFilled className={classes.infoIcon} />
            <Typography.Text>{t('addTables.rearrange')}</Typography.Text>
          </div>
        ) : null}
        <div className={classes.columnsWrapper}>
          <Typography.Title level={4} className={classes.formTitle}>
            Added Columns
          </Typography.Title>
          {pickedTableType === TableTypesEnum.Summary && (
            <Tooltip title={t('addTables.summaryInfo')}>
              <InfoCircleOutlined className={classes.infoIcon} />
            </Tooltip>
          )}
        </div>
        {pickedColumns?.length ? (
          <TableFormColumns
            columns={pickedColumns}
            isSummaryTable={pickedTableType === TableTypesEnum.Summary}
            handleDeleteColumn={handleDeleteColumn}
            handleDragColumn={(columns: string[]) => setPickedColumns(columns)}
            projectData={projectData}
          />
        ) : (
          <Typography.Text type="secondary">{t('addTables.noItems')}</Typography.Text>
        )}
        <div className={isEditForm && isMobileScreen ? classes.controls__edit : classes.controls}>
          <Button disabled={!pickedColumns?.length} onClick={clearForm} type="default">
            {t('addTables.clear')}
          </Button>
          {isEditForm ? (
            <div className={classes.editFormControls}>
              <Button onClick={stopEditCard}>{t('addTables.cancel')}</Button>
              <Form.Submit type="primary">{t('addTables.save')}</Form.Submit>
            </div>
          ) : (
            <Form.Submit type="primary">{t('addTables.add')}</Form.Submit>
          )}
        </div>
      </div>
    </ProjectsConstructorForm>
  );
};
